import React from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useBlocker } from 'react-router-dom';

const RouterPromptContext = React.createContext<{
  handleOK: () => void;
  handleCancel: () => void;
}>(Object.assign({}));

function RouterPrompt({
  blockNavigation,
  title,
  children,
  onOK = (): boolean => true,
  okText = 'Yes',
  onCancel = (): boolean => false,
  cancelText = 'No',
  hideCancelButton = false
}: {
  blockNavigation: boolean;
  onOK?: () => boolean;
  onCancel?: () => boolean;
  title: string;
  children: React.ReactNode;
  okText?: string;
  cancelText?: string;
  hideCancelButton?: boolean;
}): JSX.Element {
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      blockNavigation && !nextLocation.pathname.startsWith(currentLocation.pathname)
  );

  const handleOK = React.useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.proceed();
      onOK();
    }
  }, [blocker, onOK]);

  const handleCancel = React.useCallback(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
      onCancel();
    }
  }, [blocker, onCancel]);

  return (
    <RouterPromptContext.Provider value={{ handleOK, handleCancel }}>
      <Dialog open={blocker.state === 'blocked'}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>{children}</DialogContent>
        <DialogActions>
          {!hideCancelButton && (
            <Button onClick={handleCancel} autoFocus>
              {cancelText}
            </Button>
          )}
          <Button onClick={handleOK}>{okText}</Button>
        </DialogActions>
      </Dialog>
    </RouterPromptContext.Provider>
  );
}

export { RouterPromptContext, RouterPrompt };
